/**
 * Map engine context (§6.5 enforcement seam).
 *
 * The default engine lazy-loads the Leaflet+OSM provider as its own chunk
 * (§11.2). Swapping the whole platform to another SDK = shipping a new engine
 * here (or wrapping the app in <MapEngineProvider engine={...}>). Screens
 * never change.
 */

import { createContext, useContext, type ReactNode } from "react";
import type { CreateMap, MapEngine } from "./types";

/** Default engine: Leaflet over OpenStreetMap tiles — free, no API key (§6.5). */
export const leafletOsmEngine: MapEngine = {
  id: "leaflet-osm",
  load: (): Promise<CreateMap> =>
    import("./providers/leaflet").then((module) => module.createLeafletMap),
};

const MapEngineContext = createContext<MapEngine>(leafletOsmEngine);

export function useMapEngine(): MapEngine {
  return useContext(MapEngineContext);
}

export interface MapEngineProviderProps {
  readonly engine: MapEngine;
  readonly children: ReactNode;
}

/**
 * Override the map engine for a subtree. Production apps normally never mount
 * this (the default is the §6.5-compliant Leaflet/OSM engine); tests inject a
 * fake engine, and a future SDK swap injects its engine app-wide.
 */
export function MapEngineProvider({ engine, children }: MapEngineProviderProps) {
  return <MapEngineContext.Provider value={engine}>{children}</MapEngineContext.Provider>;
}
